
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, User, Plus, Activity, Heart, History } from "lucide-react";
import { BookingHistory } from "./BookingHistory";
import { PatientBooking } from "../PatientBooking";
import { CollapsibleCard } from "@/components/ui/collapsible-card";
import { HealthSnapshot, HealthSnapshotData } from "./HealthSnapshot";

// Mock data - in a real app this would come from your backend
const mockAppointments = [
  {
    id: 1,
    date: "2024-01-15",
    time: "10:00 AM",
    provider: "Dr. Sarah Smith",
    specialty: "Cardiology",
    status: "completed",
    reason: "Annual Checkup"
  },
  {
    id: 2,
    date: "2024-01-22",
    time: "2:30 PM",
    provider: "Dr. Michael Chen",
    specialty: "Dermatology",
    status: "completed",
    reason: "Skin Consultation"
  },
  {
    id: 3,
    date: "2024-02-05",
    time: "11:15 AM",
    provider: "Dr. Sarah Smith",
    specialty: "Cardiology",
    status: "upcoming",
    reason: "Follow-up Visit"
  }
];

const mockHealthData: HealthSnapshotData = {
  bloodPressure: "120/80",
  heartRate: "72 bpm",
  temperature: "98.6°F",
  weight: "165 lbs",
  healthScore: 85,
  lastUpdated: "2024-01-15"
};

export function PatientDashboard() {
  const [currentView, setCurrentView] = useState<'dashboard' | 'booking' | 'history'>('dashboard');

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "upcoming":
        return "bg-blue-100 text-blue-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (currentView === 'booking') {
    return (
      <div className="space-y-4">
        <Button 
          onClick={() => setCurrentView('dashboard')} 
          variant="outline"
          className="mb-4"
        >
          ← Back to Dashboard
        </Button>
        <PatientBooking />
      </div>
    );
  }

  if (currentView === 'history') {
    return (
      <div className="space-y-4">
        <Button 
          onClick={() => setCurrentView('dashboard')} 
          variant="outline"
          className="mb-4"
        >
          ← Back to Dashboard
        </Button>
        <BookingHistory />
      </div>
    );
  }

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {/* Quick Stats */}
      <CollapsibleCard
        title="Total Appointments"
        headerAction={<Calendar className="h-4 w-4 text-muted-foreground" />}
        defaultOpen={true}
        className="h-fit"
      >
        <div className="text-2xl font-bold">12</div>
        <p className="text-xs text-muted-foreground">This year</p>
      </CollapsibleCard>

      <CollapsibleCard
        title="Upcoming"
        headerAction={<Clock className="h-4 w-4 text-muted-foreground" />}
        defaultOpen={true}
        className="h-fit"
      >
        <div className="text-2xl font-bold">1</div>
        <p className="text-xs text-muted-foreground">Next: Feb 5th</p>
      </CollapsibleCard>

      <CollapsibleCard
        title="Health Score"
        headerAction={<Activity className="h-4 w-4 text-muted-foreground" />}
        defaultOpen={true}
        className="h-fit"
      >
        <div className="text-2xl font-bold">85</div>
        <p className="text-xs text-muted-foreground">Good</p>
      </CollapsibleCard>

      {/* Health Snapshot using our new component */}
      <div className="md:col-span-2 lg:col-span-1">
        <HealthSnapshot data={mockHealthData} />
      </div>

      {/* Appointment History */}
      <CollapsibleCard
        title="Recent Appointments"
        description="Your recent and upcoming appointments"
        headerAction={
          <div className="flex gap-2">
            <Button 
              onClick={() => setCurrentView('history')}
              variant="outline" 
              size="sm"
            >
              <History className="w-4 h-4 mr-2" />
              View All
            </Button>
            <Button 
              onClick={() => setCurrentView('booking')}
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Book Appointment
            </Button>
          </div>
        }
        className="md:col-span-2"
        defaultOpen={true}
      >
        <div className="space-y-4">
          {mockAppointments.slice(0, 3).map((appointment) => (
            <div key={appointment.id} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="flex flex-col">
                  <div className="font-medium">{appointment.reason}</div>
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <User className="w-3 h-3" />
                    {appointment.provider} - {appointment.specialty}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <div className="font-medium">{appointment.date}</div>
                  <div className="text-sm text-muted-foreground">{appointment.time}</div>
                </div>
                <Badge className={getStatusColor(appointment.status)}>
                  {appointment.status}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </CollapsibleCard>
    </div>
  );
}
